import { Link } from "react-router-dom";
import { Target, Mail, ArrowLeft } from "lucide-react";

const SUPPORT_EMAIL = "customersupport@curcompass.com";

function LegalShell({ title, updated, children, testid }) {
  return (
    <div className="min-h-screen" style={{ background: "#0a0a1a" }} data-testid={testid}>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0a1a]/70 border-b border-violet-500/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <img src="/logo.png" alt="Curcompass logo" className="w-8 h-8 rounded-lg object-cover" />
            <span className="font-bold text-white">Curcompass</span>
          </Link>
          <Link to="/" className="jr-btn-ghost text-xs py-2 px-3 flex items-center gap-1.5" data-testid="legal-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </Link>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">{title}</h1>
        <p className="text-xs text-zinc-500 mb-10">Last updated: {updated}</p>
        <div className="space-y-8 text-zinc-300 text-sm leading-relaxed [&_h2]:text-white [&_h2]:font-semibold [&_h2]:text-lg [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
        <div className="mt-12 jr-card text-sm text-zinc-400">
          Questions? Contact us at <a className="text-violet-300 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </div>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms & Conditions" updated="August 5, 2026" testid="terms-page">
      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>By accessing or using Curcompass ("the Service"), operated at curcompass.com, you agree to be bound by these Terms & Conditions. If you do not agree, please do not use the Service.</p>
      </section>
      <section>
        <h2>2. The Service</h2>
        <p>Curcompass is an AI-powered career assistant that provides resume parsing, job search aggregation from third-party job boards, AI resume tailoring, cover letter generation, interview preparation, salary insights, and an application tracking workspace. AI-generated content is provided as suggestions only — you are responsible for reviewing all material before using it in real applications.</p>
      </section>
      <section>
        <h2>3. Accounts & User Responsibilities</h2>
        <ul>
          <li>You must provide accurate information when creating an account (via Google sign-in or email/password).</li>
          <li>You are responsible for maintaining the confidentiality of your credentials and all activity under your account.</li>
          <li>You agree not to misuse the Service: no scraping, reverse engineering, automated bulk requests, or attempts to bypass usage limits.</li>
          <li>You must be at least 16 years old to use the Service.</li>
        </ul>
      </section>
      <section>
        <h2>4. Subscriptions & Usage</h2>
        <p>During our global launch period, the Service is offered free of charge. When paid plans are activated, the Premium plan is priced at ₹399 per 30 days (or the price displayed at checkout), billed via our payment partner Razorpay. Free accounts may be subject to usage limits which will be clearly displayed in-app. See our Refund, Cancellation & Pricing Policy for billing terms.</p>
      </section>
      <section>
        <h2>5. Third-Party Services</h2>
        <p>Job listings are aggregated from third-party sources (LinkedIn, Indeed, Glassdoor, Naukri) via public data providers. Curcompass does not guarantee the accuracy, availability, or outcome of any listing, and is not affiliated with these platforms. Applying to jobs remains subject to each platform's own terms.</p>
      </section>
      <section>
        <h2>6. Intellectual Property</h2>
        <p>All Service content, branding, and software are the property of Curcompass. Content you upload (e.g., your resume) remains yours; you grant us a limited license to process it solely to provide the Service.</p>
      </section>
      <section>
        <h2>7. Disclaimer & Limitation of Liability</h2>
        <p>The Service is provided "as is" without warranties of any kind. Curcompass does not guarantee job placement, interview calls, or salary outcomes. To the maximum extent permitted by law, our total liability is limited to the amount you paid us in the 3 months preceding the claim.</p>
      </section>
      <section>
        <h2>8. Termination</h2>
        <p>You may delete your account at any time from the Profile page. We may suspend accounts that violate these terms. Upon deletion, your data is removed in line with our Privacy Policy.</p>
      </section>
      <section>
        <h2>9. Changes</h2>
        <p>We may update these terms and will post changes on this page with a revised date. Continued use after changes constitutes acceptance.</p>
      </section>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" updated="August 5, 2026" testid="privacy-page">
      <section>
        <h2>1. Data We Collect</h2>
        <ul>
          <li><strong>Account data:</strong> name, email address, profile picture (via Google sign-in) or the name/email you register with.</li>
          <li><strong>Career data you provide:</strong> resumes you upload and the structured profile parsed from them (skills, experience, education), job preferences, saved jobs, applications, and network connections you add.</li>
          <li><strong>Usage data:</strong> feature usage counts, timestamps, and technical logs needed to operate and secure the Service.</li>
          <li><strong>Payment data:</strong> handled entirely by Razorpay — we never store your card, UPI, or bank details. We keep only order references and subscription status.</li>
        </ul>
      </section>
      <section>
        <h2>2. How We Use Your Data</h2>
        <ul>
          <li>To provide the Service: parsing your resume, scoring job matches, generating tailored documents with AI.</li>
          <li>Your resume text is processed by AI providers (Anthropic Claude) solely to generate your results; it is not used to train their models per their API terms.</li>
          <li>To send essential account emails (e.g., password reset). We do not sell your personal data — ever.</li>
        </ul>
      </section>
      <section>
        <h2>3. Storage & Security</h2>
        <p>Data is stored in secured cloud databases with encryption in transit (HTTPS/TLS). Passwords are hashed with bcrypt and never stored in plain text. Access is restricted and rate-limited to prevent abuse.</p>
      </section>
      <section>
        <h2>4. Your Rights (GDPR / Global)</h2>
        <ul>
          <li><strong>Access & portability:</strong> request a copy of your data at any time.</li>
          <li><strong>Deletion:</strong> delete your account and all associated data from the Profile page, or by emailing us. Deletion is permanent and processed immediately.</li>
          <li><strong>Correction:</strong> update your profile data in-app at any time.</li>
        </ul>
      </section>
      <section>
        <h2>5. Cookies & Sessions</h2>
        <p>We use a session token (localStorage and an httpOnly cookie) strictly for keeping you signed in. We do not use third-party advertising trackers.</p>
      </section>
      <section>
        <h2>6. Contact</h2>
        <p>For any privacy request, email customersupport@curcompass.com — we respond within 7 business days.</p>
      </section>
    </LegalShell>
  );
}

export function RefundPolicy() {
  return (
    <LegalShell title="Refund, Cancellation & Pricing Policy" updated="August 5, 2026" testid="refund-page">
      <section>
        <h2>1. Pricing</h2>
        <ul>
          <li><strong>Free plan:</strong> ₹0 — includes limited usage (shown in-app). During our global launch period, all features are free without limits.</li>
          <li><strong>Premium plan:</strong> ₹399 per 30 days (approx. $4.99 USD; final price shown at checkout). Unlocks unlimited job searches, AI resume tailoring, cover letters, mock interviews, and the Auto-Apply queue.</li>
        </ul>
      </section>
      <section>
        <h2>2. Billing Cycle</h2>
        <p>Premium is billed as a 30-day access period via Razorpay (UPI, cards, netbanking, wallets). Your plan is activated immediately after successful payment and remains active until the end of the 30-day period. We currently do not auto-charge renewals — you choose when to renew.</p>
      </section>
      <section>
        <h2>3. Cancellation</h2>
        <p>You may stop using Premium at any time; access continues until the end of your paid period. No further charges occur unless you renew manually.</p>
      </section>
      <section>
        <h2>4. Refunds</h2>
        <ul>
          <li><strong>7-day refund window:</strong> if you purchased Premium and are unsatisfied, email us within 7 days of payment for a full refund.</li>
          <li><strong>Duplicate or failed payments:</strong> automatically refunded to the original payment method within 5–7 business days.</li>
          <li>Refunds are processed via Razorpay to your original payment method. Processing time depends on your bank (typically 5–7 business days).</li>
        </ul>
      </section>
      <section>
        <h2>5. How to Request a Refund</h2>
        <p>Email customersupport@curcompass.com with your registered email and payment/order ID. We acknowledge all requests within 48 hours.</p>
      </section>
    </LegalShell>
  );
}

export function Contact() {
  return (
    <LegalShell title="Contact Us" updated="August 5, 2026" testid="contact-page">
      <section>
        <h2>Customer Support</h2>
        <p>We're a global product with a support team that responds within 24–48 hours, 7 days a week.</p>
      </section>
      <div className="jr-card flex items-center gap-4" data-testid="support-email-card">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-400 grid place-items-center shrink-0">
          <Mail className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-white font-semibold">Email Support (fastest)</div>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-violet-300 underline text-sm" data-testid="support-email-link">{SUPPORT_EMAIL}</a>
        </div>
      </div>
      <section>
        <h2>What to include</h2>
        <ul>
          <li>Your registered email address</li>
          <li>A short description of the issue (screenshots help!)</li>
          <li>For billing queries: your Razorpay order/payment ID</li>
        </ul>
      </section>
      <section>
        <h2>Business Details</h2>
        <p>Curcompass — AI Career Copilot · curcompass.com<br />Registered business address and phone support line will be published here shortly.</p>
      </section>
    </LegalShell>
  );
}
